import { FC, useMemo, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import { Autocomplete, Box, IconButton, TextField } from '@mui/material'

import { variablesToOptions } from '@mobileHelpers'
import { Variable } from '@mobileTypes'

import { VariableCreateDialog } from './VariableCreateDialog'

type VariableLike = Pick<Variable, 'internalId' | 'valueType' | 'variableType'> & {
  objectCode?: string
  isGlobal?: boolean
}

interface VariableAutocompleteWithCreateProps {
  options: VariableLike[]
  valueInternalId: string
  onChangeInternalId: (internalId: string) => void
  label: string
  helperText?: string
  required?: boolean
  disabled?: boolean
  onCreateVariable?: (name: string, isGlobal: boolean, valueType?: string, variableType?: string) => void
  createDialogTitle?: string
  createDialogDescription?: string
  showScopeSelector?: boolean
  defaultScope?: 'local' | 'global'
  defaultVariableType?: 'single' | 'multiple'
  defaultValueType?: string
  showValueTypeSelector?: boolean
  groupBy?: (option: VariableLike) => string
  getOptionLabel?: (option: VariableLike) => string
}

export const VariableAutocompleteWithCreate: FC<VariableAutocompleteWithCreateProps> = ({
  options,
  valueInternalId,
  onChangeInternalId,
  label,
  helperText,
  required = false,
  disabled = false,
  onCreateVariable,
  createDialogTitle = 'Create Variable',
  createDialogDescription,
  showScopeSelector = true,
  defaultScope = 'local',
  defaultVariableType = 'single',
  defaultValueType = 'string',
  showValueTypeSelector = true,
  groupBy,
  getOptionLabel,
}) => {
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  const existingVariableNames = useMemo(() => variablesToOptions(options as Variable[]), [options])
  const selectedOption = useMemo(
    () => options.find(v => v.internalId === valueInternalId) || null,
    [options, valueInternalId]
  )

  const resolveOptionLabel = (option: VariableLike) => {
    if (getOptionLabel) return getOptionLabel(option)
    return option.internalId
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Autocomplete
          options={options}
          groupBy={groupBy}
          getOptionLabel={resolveOptionLabel}
          value={selectedOption}
          onChange={(_, newValue) => {
            onChangeInternalId(newValue?.internalId || '')
          }}
          sx={{ flex: 1 }}
          disabled={disabled}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              helperText={helperText}
              required={required}
            />
          )}
        />
        {onCreateVariable && (
          <IconButton
            onClick={() => setShowCreateDialog(true)}
            color="primary"
            sx={{ mt: 1 }}
            title="Create new variable"
            disabled={disabled}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>

      <VariableCreateDialog
        open={showCreateDialog}
        title={createDialogTitle}
        description={createDialogDescription}
        existingVariables={existingVariableNames}
        defaultScope={defaultScope}
        showScopeSelector={showScopeSelector}
        defaultVariableType={defaultVariableType}
        defaultValueType={defaultValueType}
        showValueTypeSelector={showValueTypeSelector}
        onSave={(variable, isGlobal) => {
          onCreateVariable?.(variable.internalId, isGlobal, variable.valueType, variable.variableType)
          onChangeInternalId(variable.internalId)
          setShowCreateDialog(false)
        }}
        onClose={() => setShowCreateDialog(false)}
      />
    </>
  )
}
